import React, { PropsWithChildren, useEffect, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Stack, Grid, SelectChangeEvent, Fade, Box, IconButton } from "@mui/material"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { BillReceiversQuery, fetchAccountingBillReceivers } from "../billReceiversApi"
import * as Yup from "yup"
import { Form, Formik } from "formik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    BaseFormValues,
    BillReceiversFormValuesType,
    BillReceiversItem,
    BillReceiversType,
    BillTypeMap,
    CountryCode,
    PrivateCompanyDetailsFormValues,
    PrivatePersonDetailsFormValues,
    PublicInstitutionDetailsFormValues,
    SendInvoiceCompany,
} from "../billReceiversTypes"
import { Button } from "../../../../../office/admin/components/Button"
import { SendInvoiceCompanySection } from "./details-form/SendInvoiceCompanySection"
import { DynamicForm } from "./details-form/DynamicForm"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { SearchInvoiceSection } from "./details-form/SearchInvoiceSection"
import { isEmpty } from "lodash"
import { AddBillReceiver } from "../../../organization/userGroups/components/AddBillReceiver"
import { CreateBillTypeSelect } from "./CreateBillTypeSelect"
import { DetailsCountrySelect } from "./details-form/DetailsCountrySelect"

type ConfirmModalProps = Readonly<
    PropsWithChildren<{
        open: boolean
        onClose: () => void
        companyId: number
        title: string
        onSimpleSelect?: (billReceiver: BillReceiversItem) => void
        onSelectBillReceiver: (
            billReceiver:
                | BillReceiversFormValuesType & {
                      type: BillReceiversType | null
                      search?: string
                      companySendInvoice?: SendInvoiceCompany
                  },
        ) => void
    }>
>

type FormConfig = Readonly<{
    initialValues: BillReceiversFormValuesType
    validationSchema: Yup.AnyObject
}>

const useStyles = makeStyles()(() => ({
    dialogPaper: {
        position: "relative",
    },
    form: {
        padding: "0rem 10rem 3rem",
    },
}))

const fadeConfig = {
    timeout: 700,
    in: true,
}

const baseInitialValues: BaseFormValues = {
    country: CountryCode.DA,
    debtorNumber: "",
    addressLine1: "",
    addressLine2: "",
    postalCode: "",
    city: "",
    telephone: "",
}

const privatePersonInitialValues: PrivatePersonDetailsFormValues = {
    ...baseInitialValues,
    name: "",
}

const publicInstitutionInitialValues: PublicInstitutionDetailsFormValues = {
    ...baseInitialValues,
    institutionName: "",
    email: "",
    contactPerson: "",
}

const privateCompanyInitialValues: PrivateCompanyDetailsFormValues = {
    ...baseInitialValues,
    companyName: "",
    email: "",
    contactPerson: "",
}

export function CreateBillDialog({
    open,
    onClose,
    companyId,
    title,
    onSelectBillReceiver,
    onSimpleSelect,
}: ConfirmModalProps) {
    const { classes } = useStyles()
    const [billType, setBillType] = useState<BillReceiversType | null>(null)
    const [search, setSearch] = useState("")
    const [companySendInvoiceTo, setCompanySendInvoiceTo] = useState<SendInvoiceCompany>()
    const { l10n } = useAppLocalization()
    const [showForm, setShowForm] = useState(false)
    const [addBillReceiverModal, setAddBillReceiverModal] = useState(false)
    const [createNew, setCreateNew] = useState(false)

    const billReceivers = useApiQuery(fetchAccountingBillReceivers, {
        queryName: BillReceiversQuery.LIST,
        dependencies: {
            companyId,
            search,
        },
        enabled: false,
        select: (data) => data.billReceivers,
    })

    const handleBillTypeChange = (event: SelectChangeEvent<BillReceiversType>) => {
        const value = event.target.value as BillReceiversType
        setBillType(value)
        setSearch("")
        setShowForm(false)
        billReceivers.remove()
    }

    const onSearchClick = async () => {
        await billReceivers.refetch().then(showModal)
        if (billType !== BillReceiversType.PRIVATE_COMPANY) {
            setShowForm(true)
        }
    }

    const showModal = () => {
        if (!isEmpty(billReceivers.data)) {
            setAddBillReceiverModal(true)
        }
    }

    useEffect(() => {
        showModal()
    }, [billReceivers.data])

    const onCompanyInvoiceNext = async (item: SendInvoiceCompany) => {
        setShowForm(true)
        setCompanySendInvoiceTo(item)
    }

    const submitForm = (value: BillReceiversFormValuesType) => {
        onSelectBillReceiver({
            ...value,
            type: billType,
            search: search,
            companySendInvoice: companySendInvoiceTo,
        })
        onClose()
    }

    const onReceiverSelect = (receiver: BillReceiversItem) => {
        onSimpleSelect && onSimpleSelect(receiver)
        onClose()
    }

    const baseSchemaFields = Yup.object().shape({
        country: Yup.string().required().label(l10n.getString("bill-receivers-form-error-label-country")),
        debtorNumber: Yup.string().label(l10n.getString("bill-receivers-form-error-label-debtor-number")),
        addressLine1: Yup.string().required().label(l10n.getString("bill-receivers-form-error-label-address-line-1")),
        addressLine2: Yup.string().label(l10n.getString("bill-receivers-form-error-label-address-line-2")),
        postalCode: Yup.string().required().label(l10n.getString("bill-receivers-form-error-label-postal-code")),
        city: Yup.string().required().label(l10n.getString("bill-receivers-form-error-label-city")),
        telephone: Yup.string().required().label(l10n.getString("bill-receivers-form-error-label-telephone")),
    })

    const baseSchemaWithContactFields = baseSchemaFields.shape({
        email: Yup.string().required().email().label(l10n.getString("bill-receivers-form-error-label-email")),
        contactPerson: Yup.string().required().label(l10n.getString("bill-receivers-form-error-label-contact-person")),
    })

    const createPrivatePersonDetailsSchema = baseSchemaFields.shape({
        name: Yup.string().required().label(l10n.getString("bill-receivers-form-error-label-name")),
    })

    const createPublicInstitutionDetailsSchema = baseSchemaWithContactFields.shape({
        institutionName: Yup.string()
            .required()
            .label(l10n.getString("bill-receivers-form-error-label-institution-name")),
    })

    const createPrivateCompanyDetailsSchema = baseSchemaWithContactFields.shape({
        companyName: Yup.string().required().label(l10n.getString("bill-receivers-form-error-label-company-name")),
    })

    const formConfig: BillTypeMap<FormConfig> = {
        [BillReceiversType.PRIVATE_PERSON]: {
            initialValues: privatePersonInitialValues,
            validationSchema: createPrivatePersonDetailsSchema,
        },
        [BillReceiversType.PUBLIC_INSTITUTION]: {
            initialValues: publicInstitutionInitialValues,
            validationSchema: createPublicInstitutionDetailsSchema,
        },
        [BillReceiversType.PRIVATE_COMPANY]: {
            initialValues: privateCompanyInitialValues,
            validationSchema: createPrivateCompanyDetailsSchema,
        },
    }

    return (
        <StandardDialog
            open={open}
            scroll="body"
            onClose={onClose}
            maxWidthProp="md"
            dialogTitle={title}
            slotProps={{ paper: { className: classes.dialogPaper } }}
        >
            <Formik<BillReceiversFormValuesType>
                enableReinitialize
                initialValues={billType ? formConfig[billType].initialValues : formConfig.PRIVATE_PERSON.initialValues}
                validationSchema={
                    billType ? formConfig[billType].validationSchema : formConfig.PRIVATE_PERSON.validationSchema
                }
                onSubmit={submitForm}
            >
                <Form>
                    <Stack direction="column" spacing="1rem" className={classes.form}>
                        <DetailsCountrySelect />
                        <CreateBillTypeSelect selectedValue={billType} handleChange={handleBillTypeChange} />
                        {billType && (
                            <Fade {...fadeConfig}>
                                <Box>
                                    <SearchInvoiceSection
                                        search={search}
                                        setSearch={setSearch}
                                        onSearchCall={onSearchClick}
                                        billType={billType}
                                        isDisabled={billReceivers.isFetching}
                                    />
                                </Box>
                            </Fade>
                        )}

                        {billType === BillReceiversType.PRIVATE_COMPANY &&
                            (createNew || isEmpty(billReceivers.data)) &&
                            billReceivers.isSuccess && (
                                <Fade {...fadeConfig}>
                                    <Box>
                                        <SendInvoiceCompanySection onSearchCall={onCompanyInvoiceNext} />
                                    </Box>
                                </Fade>
                            )}

                        {billType && showForm && (createNew || isEmpty(billReceivers.data)) && (
                            <Fade {...fadeConfig}>
                                <Grid container direction="column" wrap="nowrap">
                                    <Stack spacing="1rem">
                                        <DynamicForm componentType={billType} />
                                    </Stack>

                                    <Box mt="2rem" display="flex" justifyContent="center">
                                        <LocalizedStrict id="bill-receivers-create-dialog-button-create">
                                            <Button fullWidth variant="contained" type="submit">
                                                Create
                                            </Button>
                                        </LocalizedStrict>
                                    </Box>
                                </Grid>
                            </Fade>
                        )}
                    </Stack>
                </Form>
            </Formik>

            {addBillReceiverModal && (
                <AddBillReceiver
                    type="update"
                    searchText={search}
                    companyId={companyId}
                    dialogState={addBillReceiverModal}
                    setDialogState={setAddBillReceiverModal}
                    handleChooseBillReceiver={onReceiverSelect}
                    handleCreateNewBill={() => {
                        setAddBillReceiverModal(false)
                        setCreateNew(true)
                    }}
                />
            )}
        </StandardDialog>
    )
}
